import { ChevronRight, File, Folder } from "lucide-react";
import {
  type CSSProperties,
  type RefObject,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { type NodeRendererProps, Tree } from "react-arborist";
import { createHighlighterCoreSync } from "shiki/core";
import { createJavaScriptRegexEngine } from "shiki/engine/javascript";
import makefileLanguage from "shiki/langs/makefile.mjs";
import markdownLanguage from "shiki/langs/markdown.mjs";
import shellscriptLanguage from "shiki/langs/shellscript.mjs";
import githubDarkTheme from "shiki/themes/github-dark.mjs";
import githubLightTheme from "shiki/themes/github-light.mjs";
import type { ThemedTokenWithVariants } from "shiki/types";

import { Badge } from "@/ui/badge";
import { cn } from "@/utils/tailwind";

import type {
  SourceFileContent,
  SourceFilePath,
  SourceSnapshotFileManifest,
  SourceSnapshotManifest,
} from "./types";
import { getShortCommit } from "./utils";

const codeThemes = {
  dark: "github-dark",
  light: "github-light",
} as const;
const codeHighlighter = createHighlighterCoreSync({
  engine: createJavaScriptRegexEngine(),
  langs: [makefileLanguage, markdownLanguage, shellscriptLanguage],
  themes: [githubDarkTheme, githubLightTheme],
});
export const codeFontStyle = {
  fontFamily:
    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  fontFeatureSettings: '"liga" 0, "calt" 0',
  fontVariantLigatures: "none",
  tabSize: 2,
} satisfies CSSProperties;
const sourceTextCache = new Map<string, Promise<string>>();

interface SourceBrowserProps {
  activeFilePath: SourceFilePath;
  className?: string;
  commit: string;
  highlightedLineNumbers?: readonly number[];
  manifest: SourceSnapshotManifest;
  onActiveFilePathChange: (filePath: SourceFilePath) => void;
  showFileNavigation?: boolean;
}

export function SourceBrowser({
  activeFilePath,
  className,
  commit,
  highlightedLineNumbers = [],
  manifest,
  onActiveFilePathChange,
  showFileNavigation = true,
}: SourceBrowserProps) {
  const sourceFileSelectId = useId();
  const sourceCodeRef = useRef<HTMLPreElement>(null);
  const activeFile =
    manifest.files.find((sourceFile) => {
      return sourceFile.path === activeFilePath;
    }) ?? manifest.files[0];
  const highlightedLines = useMemo(
    () => new Set(highlightedLineNumbers),
    [highlightedLineNumbers],
  );
  const sourceFileState = useSourceFileContent({
    commit,
    filePath: activeFile?.path ?? activeFilePath,
    isEnabled: Boolean(activeFile),
    sourceFileMetadata: activeFile,
  });

  useEffect(() => {
    const firstHighlightedLine = highlightedLineNumbers[0];
    if (
      sourceFileState.status !== "ready" ||
      firstHighlightedLine === undefined
    ) {
      return;
    }

    sourceCodeRef.current
      ?.querySelector(`[data-line-number="${firstHighlightedLine}"]`)
      ?.scrollIntoView({ block: "center", inline: "nearest" });
  }, [activeFilePath, highlightedLineNumbers, sourceFileState.status]);

  if (!activeFile) {
    return (
      <div className={cn("bg-card rounded-md border p-6", className)}>
        <p className="text-muted-foreground text-sm">
          This source snapshot does not contain any files.
        </p>
      </div>
    );
  }

  const activeSourceLanguage = getSourceLanguage(activeFile);

  return (
    <div
      className={cn(
        "bg-card grid h-full min-h-0 grid-rows-[minmax(0,1fr)] overflow-hidden rounded-md border shadow-sm",
        showFileNavigation &&
          "md:grid-cols-[16rem_minmax(0,1fr)] md:grid-rows-1",
        className,
      )}
    >
      {showFileNavigation && (
        <SourceFileNavigation
          activeFilePath={activeFile.path}
          className="hidden border-r md:flex"
          manifest={manifest}
          onActiveFilePathChange={onActiveFilePathChange}
        />
      )}

      <div className="flex min-h-0 min-w-0 flex-col">
        <div className="bg-card border-b">
          {showFileNavigation && (
            <div className="grid gap-2 border-b px-3 py-3 md:hidden">
              <label
                className="text-muted-foreground text-xs font-medium"
                htmlFor={sourceFileSelectId}
              >
                Source file
              </label>
              <SourceFileSelect
                activeFilePath={activeFile.path}
                id={sourceFileSelectId}
                manifest={manifest}
                onActiveFilePathChange={onActiveFilePathChange}
              />
            </div>
          )}

          <div className="flex min-h-12 flex-wrap items-center justify-between gap-3 px-4 py-2.5">
            <div className="flex min-w-0 items-center gap-2">
              <File className="text-muted-foreground size-4 shrink-0" />
              <p className="min-w-0 truncate font-mono text-xs font-medium">
                {activeFile.path}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-1.5">
              <Badge className="font-mono" variant="outline">
                {formatSourceLanguage(activeSourceLanguage)}
              </Badge>
              <Badge className="font-mono" variant="outline">
                {formatLineCount(activeFile.lineCount)}
              </Badge>
              <Badge className="font-mono" variant="outline">
                {formatByteLength(activeFile.byteLength)}
              </Badge>
            </div>
          </div>

          <p className="text-muted-foreground bg-muted/20 border-t px-4 py-3 text-sm leading-6 text-pretty">
            {activeFile.summary}
          </p>
        </div>

        <pre
          ref={sourceCodeRef}
          className="bg-background min-h-0 flex-1 overflow-auto p-0 text-[13px] leading-6"
        >
          {sourceFileState.status === "loading" && (
            <SourceStatusRow label="Loading source..." />
          )}
          {sourceFileState.status === "error" && (
            <SourceStatusRow label={sourceFileState.message} />
          )}
          {sourceFileState.status === "ready" &&
            sourceFileState.sourceFile.lines.map((line, index) => {
              const lineNumber = index + 1;
              const isHighlighted = highlightedLines.has(lineNumber);

              return (
                <code
                  key={`${activeFile.path}-${lineNumber}`}
                  data-line-number={lineNumber}
                  style={codeFontStyle}
                  className={cn(
                    "grid min-w-max grid-cols-[3.25rem_minmax(0,1fr)] font-mono",
                    isHighlighted && "bg-secondary/60",
                  )}
                >
                  <span
                    className={cn(
                      "text-muted-foreground bg-muted sticky left-0 z-10 border-r px-3 text-right select-none",
                      isHighlighted && "text-foreground",
                    )}
                  >
                    {lineNumber}
                  </span>
                  <span className="px-4 whitespace-pre">
                    <CodeLine language={activeSourceLanguage} line={line} />
                    {line.length === 0 && "\u00a0"}
                  </span>
                </code>
              );
            })}
        </pre>
      </div>
    </div>
  );
}

interface SourceFileNavigationProps {
  activeFilePath: SourceFilePath;
  className?: string;
  manifest: SourceSnapshotManifest;
  onActiveFilePathChange: (filePath: SourceFilePath) => void;
}

export function SourceFileNavigation({
  activeFilePath,
  className,
  manifest,
  onActiveFilePathChange,
}: SourceFileNavigationProps) {
  const sourceTree = buildSourceTree(manifest.files);

  return (
    <div
      className={cn("bg-muted/25 min-h-0 flex-col overflow-hidden", className)}
    >
      <div className="border-b px-3 py-3">
        <div className="flex items-center gap-2">
          <Folder className="text-muted-foreground size-4" />
          <p className="min-w-0 truncate font-mono text-xs font-medium">
            makefile-fyi
          </p>
        </div>
        <p className="text-muted-foreground mt-1 px-6 text-xs">
          {formatFileCount(manifest.files.length)}
        </p>
      </div>

      <div className="min-h-0 flex-1 p-2">
        <SourceTree
          activeFilePath={activeFilePath}
          nodes={sourceTree}
          onActiveFilePathChange={onActiveFilePathChange}
        />
      </div>
    </div>
  );
}

interface SourceFileSelectProps {
  activeFilePath: SourceFilePath;
  id?: string;
  manifest: SourceSnapshotManifest;
  onActiveFilePathChange: (filePath: SourceFilePath) => void;
}

export function SourceFileSelect({
  activeFilePath,
  id,
  manifest,
  onActiveFilePathChange,
}: SourceFileSelectProps) {
  return (
    <select
      className="border-input bg-background focus-visible:border-ring focus-visible:ring-ring/50 h-9 min-w-0 rounded-md border px-3 font-mono text-xs outline-none focus-visible:ring-[3px]"
      id={id}
      value={activeFilePath}
      onChange={(event) => {
        onActiveFilePathChange(event.target.value);
      }}
    >
      {manifest.files.map((sourceFile) => (
        <option key={sourceFile.path} value={sourceFile.path}>
          {sourceFile.path}
        </option>
      ))}
    </select>
  );
}

type SourceFileContentState =
  | {
      status: "error";
      message: string;
    }
  | {
      sourceFile: SourceFileContent;
      status: "ready";
    }
  | {
      status: "loading";
    };

export function useSourceFileContent({
  commit,
  filePath,
  isEnabled = true,
  sourceFileMetadata,
}: {
  commit: string;
  filePath: SourceFilePath;
  isEnabled?: boolean;
  sourceFileMetadata: SourceSnapshotFileManifest | undefined;
}) {
  const [sourceFileState, setSourceFileState] =
    useState<SourceFileContentState>({ status: "loading" });
  const sourceFileUrl = getSourceFileUrl({ commit, filePath });

  useEffect(() => {
    if (!isEnabled || !sourceFileMetadata) {
      return;
    }

    let isCanceled = false;

    setSourceFileState({ status: "loading" });
    getSourceText(sourceFileUrl)
      .then((sourceText) => {
        if (isCanceled) {
          return;
        }

        setSourceFileState({
          sourceFile: {
            ...sourceFileMetadata,
            lines: splitSourceText(sourceText),
          },
          status: "ready",
        });
      })
      .catch((error: unknown) => {
        if (isCanceled) {
          return;
        }

        setSourceFileState({
          message:
            error instanceof Error
              ? error.message
              : "Could not load source file.",
          status: "error",
        });
      });

    return () => {
      isCanceled = true;
    };
  }, [isEnabled, sourceFileMetadata, sourceFileUrl]);

  return sourceFileState;
}

function getSourceText(sourceFileUrl: string) {
  const cachedSourceText = sourceTextCache.get(sourceFileUrl);

  if (cachedSourceText) {
    return cachedSourceText;
  }

  const sourceTextPromise = fetch(sourceFileUrl).then((response) => {
    if (!response.ok) {
      throw new Error("Could not load source file.");
    }

    return response.text();
  });

  sourceTextCache.set(sourceFileUrl, sourceTextPromise);

  return sourceTextPromise;
}

function getSourceFileUrl({
  commit,
  filePath,
}: {
  commit: string;
  filePath: SourceFilePath;
}) {
  const encodedFilePath = filePath
    .split("/")
    .map((pathPart) => encodeURIComponent(pathPart))
    .join("/");

  return `/snapshots/${getShortCommit(commit)}/files/${encodedFilePath}`;
}

function splitSourceText(sourceText: string) {
  if (sourceText.length === 0) {
    return [];
  }

  const lineContent = sourceText.endsWith("\n")
    ? sourceText.slice(0, -1)
    : sourceText;

  return lineContent.split("\n");
}

export function SourceStatusRow({ label }: { label: string }) {
  return (
    <div
      className="text-muted-foreground bg-background grid min-w-full grid-cols-[3.25rem_minmax(0,1fr)] font-mono"
      style={codeFontStyle}
    >
      <span className="bg-muted sticky left-0 z-10 border-r px-3 text-right select-none">
        {" "}
      </span>
      <span className="px-4 py-2">{label}</span>
    </div>
  );
}

interface SourceTreeProps {
  activeFilePath: SourceFilePath;
  nodes: readonly SourceTreeNode[];
  onActiveFilePathChange: (filePath: SourceFilePath) => void;
}

function SourceTree({
  activeFilePath,
  nodes,
  onActiveFilePathChange,
}: SourceTreeProps) {
  const treeContainerRef = useRef<HTMLDivElement>(null);
  const treeHeight = useSourceTreeHeight(treeContainerRef);

  return (
    <div
      ref={treeContainerRef}
      className="h-full min-h-0 overflow-hidden rounded-md"
    >
      <Tree<SourceTreeNode>
        disableDrag
        disableDrop
        disableEdit
        disableMultiSelection
        openByDefault
        data={nodes}
        height={treeHeight}
        idAccessor={(node) => node.path}
        indent={0}
        overscanCount={8}
        rowHeight={32}
        selection={activeFilePath}
        width="100%"
        childrenAccessor={(node) => {
          return node.kind === "directory" ? node.children : null;
        }}
        onActivate={(node) => {
          if (node.data.kind === "file") {
            onActiveFilePathChange(node.data.path);
            return;
          }

          node.toggle();
        }}
      >
        {(props) => <SourceTreeNodeView {...props} />}
      </Tree>
    </div>
  );
}

function SourceTreeNodeView({
  node,
  style,
}: NodeRendererProps<SourceTreeNode>) {
  return (
    <button
      aria-current={node.isSelected ? "true" : undefined}
      style={style}
      type="button"
      className={cn(
        "flex w-full items-center rounded-sm pr-2 text-left text-sm transition",
        node.isSelected
          ? "bg-background text-foreground ring-border/70 shadow-xs ring-1"
          : "text-muted-foreground hover:bg-background/70 hover:text-foreground",
      )}
      onClick={(event) => {
        event.stopPropagation();

        if (node.data.kind === "directory") {
          node.toggle();
          return;
        }

        node.activate();
      }}
    >
      <span
        className="flex min-w-0 items-center gap-1.5"
        style={{ paddingInlineStart: `${0.5 + node.level * 0.5}rem` }}
      >
        {node.data.kind === "directory" ? (
          <>
            <ChevronRight
              className={cn(
                "size-3.5 shrink-0 transition-transform",
                node.isOpen && "rotate-90",
              )}
            />
            <Folder className="size-4 shrink-0" />
          </>
        ) : (
          <>
            <span className="size-3.5 shrink-0" />
            <File className="size-4 shrink-0" />
          </>
        )}
        <span className="min-w-0 truncate font-mono text-xs">
          {node.data.name}
        </span>
      </span>
    </button>
  );
}

function useSourceTreeHeight(
  treeContainerRef: RefObject<HTMLDivElement | null>,
) {
  const [treeHeight, setTreeHeight] = useState(520);

  useEffect(() => {
    const treeContainer = treeContainerRef.current;
    if (!treeContainer) {
      return;
    }

    function updateTreeHeight() {
      setTreeHeight(treeContainer.clientHeight);
    }

    updateTreeHeight();
    const resizeObserver = new ResizeObserver(updateTreeHeight);
    resizeObserver.observe(treeContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, [treeContainerRef]);

  return treeHeight;
}

interface SourceTreeDirectoryNode {
  children: SourceTreeNode[];
  kind: "directory";
  name: string;
  path: string;
}

interface SourceTreeFileNode {
  kind: "file";
  name: string;
  path: SourceFilePath;
}

type SourceTreeNode = SourceTreeDirectoryNode | SourceTreeFileNode;

function buildSourceTree(sourceFiles: readonly SourceSnapshotFileManifest[]) {
  const root: SourceTreeDirectoryNode = {
    children: [],
    kind: "directory",
    name: "makefile-fyi",
    path: "",
  };

  for (const sourceFile of sourceFiles) {
    const pathParts = sourceFile.path.split("/");
    let directoryNode = root;

    pathParts.forEach((pathPart, index) => {
      const isFile = index === pathParts.length - 1;
      const nodePath = pathParts.slice(0, index + 1).join("/");

      if (isFile) {
        directoryNode.children.push({
          kind: "file",
          name: pathPart,
          path: sourceFile.path,
        });
        return;
      }

      let nextDirectoryNode = directoryNode.children.find(
        (childNode): childNode is SourceTreeDirectoryNode =>
          childNode.kind === "directory" && childNode.name === pathPart,
      );

      if (!nextDirectoryNode) {
        nextDirectoryNode = {
          children: [],
          kind: "directory",
          name: pathPart,
          path: nodePath,
        };
        directoryNode.children.push(nextDirectoryNode);
      }

      directoryNode = nextDirectoryNode;
    });
  }

  sortSourceTreeNodes(root.children);

  return root.children;
}

function sortSourceTreeNodes(nodes: SourceTreeNode[]) {
  nodes.sort((firstNode, secondNode) => {
    if (firstNode.kind !== secondNode.kind) {
      return firstNode.kind === "directory" ? -1 : 1;
    }

    return firstNode.name.localeCompare(secondNode.name);
  });

  for (const node of nodes) {
    if (node.kind === "directory") {
      sortSourceTreeNodes(node.children);
    }
  }
}

export function CodeLine({
  language,
  line,
}: {
  language: SourceSnapshotFileManifest["language"];
  line: string;
}) {
  const tokens = getCodeLineTokens(language, line);

  return (
    <>
      {tokens.map((token, index) => (
        <CodeToken
          key={`${token.offset}-${token.content}-${index}`}
          token={token}
        />
      ))}
    </>
  );
}

function getCodeLineTokens(
  language: SourceSnapshotFileManifest["language"],
  line: string,
) {
  if (line.length === 0) {
    return [];
  }

  return (
    codeHighlighter.codeToTokensWithThemes(line, {
      lang: language,
      themes: codeThemes,
    })[0] ?? []
  );
}

function CodeToken({ token }: { token: ThemedTokenWithVariants }) {
  const darkVariant = token.variants["dark"];
  const lightVariant = token.variants["light"];
  const style = {
    "--shiki-dark": darkVariant?.color ?? "currentColor",
    "--shiki-light": lightVariant?.color ?? "currentColor",
  } as CSSProperties;

  return (
    <span
      className="text-[var(--shiki-light)] dark:text-[var(--shiki-dark)]"
      style={style}
    >
      {token.content}
    </span>
  );
}

export function getLineRange(lineNumbers: readonly number[]) {
  const uniqueLineNumbers = Array.from(new Set(lineNumbers)).sort(
    (first, second) => first - second,
  );

  if (uniqueLineNumbers.length === 0) {
    return undefined;
  }

  if (uniqueLineNumbers.length === 1) {
    return String(uniqueLineNumbers[0]);
  }

  return `${uniqueLineNumbers[0]}-${uniqueLineNumbers.at(-1)}`;
}

// Older snapshot manifests classify included `.mk` files as plain text. Derive
// the effective language from conventional filenames so all snapshots receive
// the same highlighting and comment behavior as a root `Makefile`.
export function getSourceLanguage(
  sourceFile: Pick<SourceSnapshotFileManifest, "language" | "path">,
) {
  const fileName = sourceFile.path.split("/").at(-1);

  if (
    sourceFile.path.endsWith(".mk") ||
    fileName === "GNUmakefile" ||
    fileName === "makefile" ||
    fileName === "Makefile"
  ) {
    return "makefile" as const;
  }

  if (sourceFile.path.endsWith(".sh")) {
    return "shellscript" as const;
  }

  return sourceFile.language;
}

function formatFileCount(fileCount: number) {
  return `${fileCount} ${fileCount === 1 ? "file" : "files"}`;
}

function formatLineCount(lineCount: number) {
  return `${lineCount} ${lineCount === 1 ? "line" : "lines"}`;
}

function formatByteLength(byteLength: number) {
  if (byteLength < 1024) {
    return `${byteLength} B`;
  }

  return `${(byteLength / 1024).toFixed(1)} KB`;
}

function formatSourceLanguage(
  language: SourceSnapshotFileManifest["language"],
) {
  switch (language) {
    case "makefile": {
      return "Makefile";
    }
    case "markdown": {
      return "Markdown";
    }
    case "shellscript": {
      return "Shell";
    }
    case "text": {
      return "Text";
    }
  }
}
